import React, { useState, useEffect } from 'react';
import { Search, X, ChevronRight, ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { fetchAPI } from '@/core/api/client';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { getAbsoluteMediaUrl } from '@/core/utils/media';
import {
    activeFilterCount,
    appendFacetFilters,
    humanizeFacetKey,
    toggleFacetValue,
    type FacetsResponse,
    type FiltersState,
} from './catalog-filter-utils';

export type { FiltersState } from './catalog-filter-utils';

export interface FiltersStyleConfig {
    titleText?: string;
    clearText?: string;
    searchLabel?: string;
    searchPlaceholder?: string;
    priceLabel?: string;
    subcategoriesLabel?: string;
    allSubcategoriesText?: string;
    titleColor?: string;
    titleFontSize?: number;
    titleFontWeight?: string;
    clearColor?: string;
    labelColor?: string;
    labelFontSize?: number;
    inputBackgroundColor?: string;
    inputBorderColor?: string;
    inputTextColor?: string;
    inputBorderRadius?: number;
    pillTextColor?: string;
    pillActiveBackgroundColor?: string;
    pillActiveTextColor?: string;
    pillHoverBackgroundColor?: string;
    resultCountColor?: string;
    resultCountFontSize?: number;
    /** Subcategories shown in their own colour (comma-separated names or slugs, case-insensitive),
     *  e.g. "Minikoparki" — so the range the shop focuses on stands out in the list. */
    highlightSubcategories?: string;
    highlightBackgroundColor?: string;
    highlightTextColor?: string;
}

const DEFAULT_STYLE: Required<FiltersStyleConfig> = {
    titleText: 'Filtry',
    clearText: 'Wyczyść',
    searchLabel: 'Wyszukiwarka',
    searchPlaceholder: 'Szukaj produktów…',
    priceLabel: 'Cena (PLN)',
    subcategoriesLabel: 'Podkategorie',
    allSubcategoriesText: 'Wszystkie',
    titleColor: '',
    titleFontSize: 0,
    titleFontWeight: '',
    clearColor: '',
    labelColor: '',
    labelFontSize: 0,
    inputBackgroundColor: '',
    inputBorderColor: '',
    inputTextColor: '',
    inputBorderRadius: -1,
    pillTextColor: '',
    pillActiveBackgroundColor: '',
    pillActiveTextColor: '',
    pillHoverBackgroundColor: '',
    resultCountColor: '',
    resultCountFontSize: 0,
    highlightSubcategories: '',
    highlightBackgroundColor: '',
    highlightTextColor: '',
};

interface SidebarFiltersProps {
    initialFilters: FiltersState;
    onFiltersChange: (filters: FiltersState) => void;
    subcategories?: Array<{ id: number; name: string; slug: string }>;
    categoryPath?: string;
    productCount?: number;
    styleConfig?: FiltersStyleConfig;
    hideHeader?: boolean;
    categoryId?: number | null;
    onFacetsChange?: (data: FacetsResponse | null) => void;
    previewFacetData?: FacetsResponse;
}

export function SidebarFilters({
    initialFilters,
    onFiltersChange,
    subcategories = [],
    productCount = 0,
    styleConfig,
    hideHeader = false,
    categoryId,
    onFacetsChange,
    previewFacetData,
}: SidebarFiltersProps) {
    const { t, currency } = useTranslation();
    const siteConfig = useSiteConfig();
    const catalogFilteringEnabled = Boolean(previewFacetData) || (Array.isArray(siteConfig.catalog_filters) && siteConfig.catalog_filters.length > 0);
    const cfg = { ...DEFAULT_STYLE, ...styleConfig };
    const [showAllSubcategories, setShowAllSubcategories] = useState(false);
    const [facetData, setFacetData] = useState<FacetsResponse | null>(previewFacetData || null);

    const titleText = (styleConfig?.titleText && styleConfig.titleText !== 'Filtry') ? styleConfig.titleText : t('filters.title', 'Filtry');
    const clearText = (styleConfig?.clearText && styleConfig.clearText !== 'Wyczyść') ? styleConfig.clearText : t('filters.clear', 'Wyczyść');
    const searchLabel = (styleConfig?.searchLabel && styleConfig.searchLabel !== 'Wyszukiwarka') ? styleConfig.searchLabel : t('filters.search', 'Wyszukiwarka');
    const searchPlaceholder = (styleConfig?.searchPlaceholder && styleConfig.searchPlaceholder !== 'Szukaj produktów…') ? styleConfig.searchPlaceholder : t('filters.search_placeholder', 'Szukaj produktów…');
    const priceLabel = (styleConfig?.priceLabel && !styleConfig.priceLabel.startsWith('Cena')) ? styleConfig.priceLabel : t('filters.price', `Cena (${currency})`).replace('{currency}', currency);
    const subcategoriesLabel = (styleConfig?.subcategoriesLabel && styleConfig.subcategoriesLabel !== 'Podkategorie') ? styleConfig.subcategoriesLabel : t('filters.subcategories', 'Podkategorie');
    const allSubcategoriesText = (styleConfig?.allSubcategoriesText && styleConfig.allSubcategoriesText !== 'Wszystkie') ? styleConfig.allSubcategoriesText : t('filters.all_subcategories', 'Wszystkie');

    const labelStyle: React.CSSProperties = {
        color: cfg.labelColor || undefined,
        fontSize: cfg.labelFontSize ? `${cfg.labelFontSize}px` : undefined,
    };
    const inputStyle: React.CSSProperties = {
        backgroundColor: cfg.inputBackgroundColor || undefined,
        borderColor: cfg.inputBorderColor || undefined,
        color: cfg.inputTextColor || undefined,
        borderRadius: cfg.inputBorderRadius >= 0 ? `${cfg.inputBorderRadius}px` : undefined,
    };
    const pillStyle = (active: boolean): React.CSSProperties => ({
        color: active ? (cfg.pillActiveTextColor || undefined) : (cfg.pillTextColor || undefined),
        backgroundColor: active ? (cfg.pillActiveBackgroundColor || undefined) : undefined,
    });

    const highlighted = new Set(
        (cfg.highlightSubcategories || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
    );
    const isHighlighted = (sub: { name: string; slug: string }) =>
        highlighted.has(sub.name.trim().toLowerCase()) || highlighted.has(sub.slug.toLowerCase());
    // A highlighted, not-selected subcategory: its own colours at full opacity (selection still wins).
    const highlightStyle: React.CSSProperties = {
        color: cfg.highlightTextColor || '#92400E',
        backgroundColor: cfg.highlightBackgroundColor || '#FEF3C7',
        opacity: 1,
        fontWeight: 700,
    };

    const [filters, setFilters] = useState<FiltersState>(initialFilters);
    const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setFilters(initialFilters);
    }, [initialFilters]);

    const update = (patch: Partial<FiltersState>, debounce = false) => {
        const next = { ...filters, ...patch };
        setFilters(next);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (debounce) {
            debounceTimerRef.current = setTimeout(() => {
                onFiltersChange(next);
            }, 350);
        } else {
            onFiltersChange(next);
        }
    };

    const hasActiveFilters = activeFilterCount(filters) > 0;

    useEffect(() => {
        if (previewFacetData) {
            setFacetData(previewFacetData);
            return;
        }
        if (!catalogFilteringEnabled) {
            setFacetData(null);
            onFacetsChange?.(null);
            return;
        }
        let active = true;
        const params = new URLSearchParams();
        const effectiveCategoryId = filters.subcategoryId ?? categoryId;
        if (effectiveCategoryId) params.set('category_id', String(effectiveCategoryId));
        if (filters.query.trim()) params.set('search', filters.query.trim());
        if (filters.minPrice) params.set('min_price', filters.minPrice);
        if (filters.maxPrice) params.set('max_price', filters.maxPrice);
        appendFacetFilters(params, filters.facets);

        fetchAPI(`/products/facets?${params.toString()}`)
            .then((data) => {
                if (!active) return;
                const next = data && Array.isArray(data.facets) ? data as FacetsResponse : null;
                setFacetData(next);
                onFacetsChange?.(next);
            })
            .catch(() => {
                if (!active) return;
                setFacetData(null);
                onFacetsChange?.(null);
            });
        return () => { active = false; };
    }, [catalogFilteringEnabled, categoryId, filters, onFacetsChange, previewFacetData]);

    const clearAll = () => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        const cleared: FiltersState = { query: '', minPrice: '', maxPrice: '', subcategoryId: null, facets: {} };
        setFilters(cleared);
        onFiltersChange(cleared);
    };

    const hoverClass = 'sidebar-filters-pill-hover';

    return (
        <aside className="space-y-6 w-full text-current">
            {cfg.pillHoverBackgroundColor && (
                <style>{`.${hoverClass}:hover { background-color: ${cfg.pillHoverBackgroundColor} !important; }`}</style>
            )}

            {/* Header is supplied by mobile drawers, so they can keep the close action
                and title on one line without rendering a second "Filtry" heading. */}
            {!hideHeader && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <SlidersHorizontal className="w-4 h-4 text-current opacity-60" style={{ color: cfg.titleColor || undefined }} />
                        <span
                            className="text-xs font-black uppercase tracking-widest text-current opacity-60"
                            style={{
                                color: cfg.titleColor || undefined,
                                fontSize: cfg.titleFontSize ? `${cfg.titleFontSize}px` : undefined,
                                fontWeight: cfg.titleFontWeight || undefined,
                            }}
                        >
                            {titleText}
                        </span>
                        {hasActiveFilters && (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-[9px] font-black">
                                {activeFilterCount(filters)}
                            </span>
                        )}
                    </div>
                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={clearAll}
                            className="text-[10px] font-bold text-current opacity-40 hover:opacity-100 hover:text-red-500 flex items-center gap-1 transition-colors"
                            style={{ color: cfg.clearColor || undefined }}
                        >
                            <X className="w-3 h-3" />
                            {clearText}
                        </button>
                    )}
                </div>
            )}

            {/* Search */}
            <div className="space-y-2">
                <p className="text-[10px] font-black uppercase tracking-widest text-current opacity-50" style={labelStyle}>{searchLabel}</p>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-current opacity-40 pointer-events-none" />
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={filters.query}
                        onChange={(e) => update({ query: e.target.value }, true)}
                        className="w-full pl-9 pr-8 py-2.5 text-sm border border-current/15 rounded-xl bg-current/[0.03] text-current focus:bg-current/[0.01] focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none transition-all placeholder:text-current/30"
                        style={inputStyle}
                    />
                    {filters.query && (
                        <button
                            type="button"
                            onClick={() => update({ query: '' }, false)}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-current/10 transition-colors"
                        >
                            <X className="w-3 h-3 text-current opacity-40" />
                        </button>
                    )}
                </div>
            </div>

            {/* Price Range */}
            <div className="space-y-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-current opacity-50" style={labelStyle}>{priceLabel}</p>
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-current opacity-40 font-medium">{t('filters.price_from', 'od')}</span>
                        <input
                            type="number"
                            min="0"
                            placeholder={facetData?.price.min !== null && facetData?.price.min !== undefined ? String(Math.floor(facetData.price.min)) : '0'}
                            value={filters.minPrice}
                            onChange={(e) => update({ minPrice: e.target.value }, true)}
                            className="w-full pl-8 pr-3 py-2 text-sm border border-current/15 rounded-xl bg-current/[0.03] text-current focus:bg-current/[0.01] focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none transition-all"
                            style={inputStyle}
                        />
                    </div>
                    <span className="text-current opacity-30 font-black text-lg">—</span>
                    <div className="flex-1 relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-current opacity-40 font-medium">{t('filters.price_to', 'do')}</span>
                        <input
                            type="number"
                            min="0"
                            placeholder={facetData?.price.max !== null && facetData?.price.max !== undefined ? String(Math.ceil(facetData.price.max)) : '∞'}
                            value={filters.maxPrice}
                            onChange={(e) => update({ maxPrice: e.target.value }, true)}
                            className="w-full pl-8 pr-3 py-2 text-sm border border-current/15 rounded-xl bg-current/[0.03] text-current focus:bg-current/[0.01] focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none transition-all"
                            style={inputStyle}
                        />
                    </div>
                </div>
                {(filters.minPrice || filters.maxPrice) && (
                    <p className="text-[10px] text-primary font-semibold">
                        {t('filters.filtering_label', 'Filtrowanie')}: {filters.minPrice || '0'} — {filters.maxPrice || '∞'} {currency}
                    </p>
                )}
            </div>

            {/* a filter with no values in this category (e.g. needle count among spare parts) would be a bare heading */}
            {facetData?.facets.filter((facet) => facet.values.length > 0).map((facet) => {
                const selected = filters.facets[facet.key] || [];
                const isSize = facet.key === 'rozmiar' || facet.key === 'size';
                const title = facet.label ? facet.label : facet.key === '@brand'
                    ? t('filters.brand', 'Marka')
                    : facet.key === '@condition'
                        ? t('filters.condition', 'Stan')
                        : isSize ? t('filters.size', 'Rozmiar') : humanizeFacetKey(facet.key);
                return (
                    <div key={facet.key} className="space-y-2">
                        <p className="text-[10px] font-black uppercase tracking-widest text-current opacity-50" style={labelStyle}>{title}</p>
                        <div className={cn(isSize ? 'flex flex-wrap gap-2' : 'space-y-1')}>
                            {facet.values.map((item) => {
                                const checked = selected.includes(item.value);
                                const label = facet.key === '@condition'
                                    ? t(`product.condition.${item.value}`, item.label)
                                    : item.label;
                                if (isSize) {
                                    return (
                                        <button
                                            key={item.value}
                                            type="button"
                                            aria-pressed={checked}
                                            onClick={() => update({ facets: toggleFacetValue(filters.facets, facet.key, item.value) })}
                                            className={cn(
                                                hoverClass,
                                                'min-w-11 rounded-lg border px-3 py-2 text-xs font-bold transition-colors',
                                                checked ? 'border-primary bg-primary text-white' : 'border-current/15 hover:border-primary/50',
                                            )}
                                            style={pillStyle(checked)}
                                        >
                                            {label}
                                        </button>
                                    );
                                }
                                return (
                                    <label key={item.value} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-current/5">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => update({ facets: toggleFacetValue(filters.facets, facet.key, item.value) })}
                                            className="h-4 w-4 rounded border-current/20 accent-primary"
                                        />
                                        {item.logo_url && <img src={getAbsoluteMediaUrl(item.logo_url)} alt="" className="h-5 w-5 object-contain" />}
                                        <span className="min-w-0 flex-1 truncate">{label}</span>
                                        <span className="text-xs opacity-45">{item.count}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Subcategories */}
            {subcategories.length > 0 && (
                <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-current opacity-50" style={labelStyle}>{subcategoriesLabel}</p>
                    <ul className="space-y-0.5">
                        <li>
                            <button
                                type="button"
                                onClick={() => update({ subcategoryId: null })}
                                className={cn(
                                    hoverClass,
                                    'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all font-medium',
                                    filters.subcategoryId === null
                                        ? 'bg-primary/10 text-primary font-bold'
                                        : 'text-current opacity-70 hover:bg-current/5 hover:text-current hover:opacity-100'
                                )}
                                style={pillStyle(filters.subcategoryId === null)}
                            >
                                <span>{allSubcategoriesText}</span>
                                {filters.subcategoryId === null && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                )}
                            </button>
                        </li>
                        {(showAllSubcategories ? subcategories : subcategories.slice(0, 8)).map((sub) => (
                            <li key={sub.id}>
                                <button
                                    type="button"
                                    onClick={() => update({ subcategoryId: sub.id })}
                                    className={cn(
                                        hoverClass,
                                        'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all',
                                        filters.subcategoryId === sub.id
                                            ? 'bg-primary/10 text-primary font-bold'
                                            : 'text-current opacity-70 hover:bg-current/5 hover:text-current hover:opacity-100'
                                    )}
                                    style={filters.subcategoryId !== sub.id && isHighlighted(sub)
                                        ? highlightStyle
                                        : pillStyle(filters.subcategoryId === sub.id)}
                                >
                                    <span className="flex items-center gap-1.5">
                                        <ChevronRight className="w-3 h-3 opacity-50 flex-shrink-0" />
                                        {sub.name}
                                    </span>
                                    {filters.subcategoryId === sub.id && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {subcategories.length > 8 && (
                        <button
                            type="button"
                            onClick={() => setShowAllSubcategories(!showAllSubcategories)}
                            className="text-xs font-semibold text-primary hover:underline pt-1 flex items-center gap-1"
                        >
                            <span>
                                {showAllSubcategories
                                    ? t('filters.show_less', 'Zwiń')
                                    : `${t('filters.show_more', 'Pokaż więcej')} (${subcategories.length - 8})`}
                            </span>
                            {showAllSubcategories ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                    )}
                </div>
            )}

            {/* Result count hint */}
            <div className="pt-2 border-t border-current/10">
                <p
                    className="text-[10px] text-current opacity-40 text-center"
                    style={{
                        color: cfg.resultCountColor || undefined,
                        fontSize: cfg.resultCountFontSize ? `${cfg.resultCountFontSize}px` : undefined,
                    }}
                >
                    {t('category.products_count', '{n} produktów').replace('{n}', String(productCount))}
                </p>
            </div>
        </aside>
    );
}
